import { EmptyState, LoadingState, VButton, VDialog } from '@vrzl/ui';
import { useAtom } from 'jotai';
import { Check, ExternalLink, Share2, FileQuestion } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useDocument } from '../api/queries';
import { resolveDocumentKind } from '../lib/documentKind';
import type { EvidenceSelection } from '../lib/evidenceSelection';
import { documentDialogAtom } from '../state/documentDialog.atom';

import { DocumentDocxPanel } from './documentDocxPanel';
import { DocumentPdfPanel } from './documentPdfPanel';
import { DocumentResultsPanel } from './documentResultsPanel';
import { DocumentSummary } from './documentSummary';

function buildFullPageUrl(
  eventId: string,
  docId: string,
  initialPages?: number[],
  highlightQuery?: string,
): string {
  const params = new URLSearchParams();
  if (initialPages && initialPages.length > 0) {
    params.set('pages', initialPages.join(','));
  }
  if (highlightQuery) params.set('q', highlightQuery);
  const qs = params.toString();
  return `/events/${eventId}/documents/${docId}${qs ? `?${qs}` : ''}`;
}

export function DocumentDialog() {
  const [state, setState] = useAtom(documentDialogAtom);
  const [selected, setSelected] = useState<EvidenceSelection | null>(null);
  const [copied, setCopied] = useState(false);

  // Elke opening (ook opnieuw van hetzelfde document) begint op de aangeklikte passage.
  useEffect(() => {
    setSelected(state?.initialEvidence ?? null);
  }, [state]);

  const selectedEvidence = selected?.evidence ?? null;

  const isOpen = state !== null;
  const eventId = state?.eventId ?? '';
  const docId = state?.docId ?? '';
  const initialPages = state?.initialPages;
  const highlightQuery = state?.highlightQuery;

  const { data: doc, isPending: isDocPending } = useDocument(eventId, docId);
  const kind = resolveDocumentKind(doc?.sourceFileName);

  const handleOpenInNewTab = () => {
    if (!state) return;
    const url = buildFullPageUrl(
      state.eventId,
      state.docId,
      state.initialPages,
      state.highlightQuery,
    );
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleShare = async () => {
    if (!state) return;
    const url = buildFullPageUrl(
      state.eventId,
      state.docId,
      state.initialPages,
      state.highlightQuery,
    );
    await navigator.clipboard.writeText(`${window.location.origin}${url}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <VDialog
      open={isOpen}
      onOpenChange={(open) => !open && setState(null)}
      size="xl"
      title={doc?.name ?? 'Document'}
      titleHidden
      description="Document weergave met gevonden resultaten."
      descriptionHidden
      headerStart={doc && <DocumentSummary document={doc} />}
      header={
        <div className="flex items-center gap-1">
          <VButton
            variant="ghost"
            size="sm"
            onClick={handleOpenInNewTab}
            className="inline-flex items-center gap-2"
          >
            <ExternalLink className="size-4" />
            Open in nieuwe tab
          </VButton>
          <VButton
            variant="ghost"
            size="sm"
            onClick={handleShare}
            className="inline-flex items-center gap-2"
          >
            {copied ? <Check className="size-4" /> : <Share2 className="size-4" />}
            {copied ? 'Gekopieerd!' : 'Delen'}
          </VButton>
        </div>
      }
    >
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-hidden">
          {state && isDocPending && <LoadingState message="Document laden..." />}

          {state && !isDocPending && kind === 'docx' && (
            <DocumentDocxPanel
              eventId={eventId}
              docId={docId}
              selectedEvidence={selectedEvidence}
            />
          )}

          {state && !isDocPending && kind === 'pdf' && (
            <DocumentPdfPanel
              eventId={eventId}
              docId={docId}
              initialPages={initialPages}
              highlightQuery={highlightQuery}
              selectedEvidence={selectedEvidence}
            />
          )}

          {state && !isDocPending && kind === 'unknown' && (
            <div className="flex h-full items-center justify-center">
              <EmptyState
                icon={FileQuestion}
                title="Documenttype wordt niet ondersteund"
                description="Dit bestand kan niet in de viewer worden weergegeven. Ondersteund zijn PDF- en Word-documenten (.docx)."
              />
            </div>
          )}
        </div>
        <aside className="w-[400px] shrink-0 border-l border-border/50">
          {state && (
            <DocumentResultsPanel
              eventId={eventId}
              docUrl={doc?.url}
              selected={selected}
              onSelect={(item) => setSelected({ criterionId: item.criterionId, evidence: item })}
            />
          )}
        </aside>
      </div>
    </VDialog>
  );
}
